import { PhoneOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Col, Modal, Row, Space, Typography, Avatar } from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useGetAdvertDetails } from "../ServerSideStates";
import dayjs from "dayjs";
import PriceFormatter from "apps/Common/PriceFormatter";
import { useEffect, useState, useCallback, useMemo } from "react";
import CreateContactInfoes from "./CreateContactInfoes";
import ComplaintForm from "./Complaint";
import SendMessage from "./SendMessage";
import CategoryBreadCrumb from "./CategoryBreadcrumb";
import { useSelector } from "react-redux";
import { RootState } from "store/Reducers";
import { openNotificationWithIcon } from "helpers/OpenNotificationWithIcon";
import useCapitalize from "apps/Common/Adress/helpers/useCapitalize";
import Options from "./Options/OptionsIndex";
import { Link } from "react-router-dom";
import { getAdvertListFilter } from "../../../News/Services";
import { setBackEndUlr } from "helpers/SetBackEndUrl";

// Interfaces
interface OptionItem {
  optionId: number;
  optionName: string;
  type: number;
  selectedValue?: string;
  optionValueName?: string;
  optionValueId?: number;
}

interface FormattedOption {
  name: string;
  value: any;
  type?: string;
  key: number;
  values?: any[];
}

interface FilterParams {
  CategoryId?: number;
  CityId?: number;
  DistrictIds?: number[];
  NeighbourhoodIds?: number[];
  IncludeProperties?: string[];
}

const RightInfoDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const advertId = searchParams.get("id");
  const { userInfoes } = useSelector((state: RootState) => state.profile);
  const advertDetails = useGetAdvertDetails(advertId);

  const data = advertDetails?.data?.data;

  const { capitalize } = useCapitalize();
  const [optionsData, setOptionsData] = useState<FormattedOption[]>([]);
  const [isShowAskQuesModal, setIsShowAskQuesModal] = useState(false);
  const [isShowCreateContactModal, setIsShowCreateContactModal] = useState(false);
  const [isShowComplaintModal, setIsShowComplaintModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { Text } = Typography;

  // Safe API caller - undefined ve null değerleri filtreler
  const getAdvertListFilterSafe = useCallback((params: FilterParams) => {
    // Undefined ve null değerleri filtrele
    const cleanParams = Object.entries(params).reduce((acc: any, [key, value]) => {
      if (value !== undefined && value !== null && value !== '' && 
          !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Eğer temiz parametreler yoksa API çağrısı yapma
    if (Object.keys(cleanParams).length === 0) {
      return Promise.reject(new Error('Geçerli parametre bulunamadı'));
    }
    
    return getAdvertListFilter(cleanParams);
  }, []);

  // Memoized slugify function
  const slugify = useCallback((str = '') =>
    str
      .toLowerCase()
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ü/g, 'u')
      .replace(/\s+/g, '')
      .replace(/[^\w-]+/g, ''), []);

  // Memoized URL creation
  const { categorySlug, citySlug, districtSlug, neighbourhoodSlug, fullUrl } = useMemo(() => {
    if (!data) return { categorySlug: '', citySlug: '', districtSlug: '', neighbourhoodSlug: '', fullUrl: '' };

    const categorySlug = data?.category?.url ? slugify(data.category.url) : '';
    const citySlug = data?.city?.name ? slugify(data.city.name) : '';
    const districtSlug = data?.district?.name ? slugify(data.district.name) : '';
    const neighbourhoodSlug = data?.neighbourhood?.name ? slugify(data.neighbourhood.name) : '';

    const path = `/ilan/${categorySlug}/${citySlug}-${districtSlug}-${neighbourhoodSlug}`;
    return {
      categorySlug,
      citySlug,
      districtSlug,
      neighbourhoodSlug,
      fullUrl: path
    };
  }, [data, slugify]);

  // Options formatting function with proper typing
  const handleFormattedDBOptions = useCallback((optionList: OptionItem[]): FormattedOption[] => {
    const formattedOptionsList: FormattedOption[] = [];
    const multipleSelectMap: Record<number, FormattedOption> = {};

    for (const opItem of optionList) {
      if (opItem?.type === 1) {
        formattedOptionsList.push({
          name: opItem.optionName,
          value: opItem.selectedValue,
          type: "input",
          key: opItem.optionId,
        });
      } else if (opItem?.type === 2) {
        formattedOptionsList.push({
          name: opItem.optionName,
          value: {
            name: opItem.optionValueName,
            value: opItem.optionValueId,
            type: "select",
            key: opItem.optionValueId,
          },
          key: opItem.optionId,
        });
      } else if (opItem?.type === 3) {
        if (!multipleSelectMap[opItem.optionId]) {
          multipleSelectMap[opItem.optionId] = {
            name: opItem.optionName,
            value: null,
            values: [],
            key: opItem.optionId,
          };
        }
        multipleSelectMap[opItem.optionId].values = multipleSelectMap[opItem.optionId].values || [];
        multipleSelectMap[opItem.optionId].values!.push({
          name: opItem.optionValueName,
          value: opItem.selectedValue,
          key: opItem.optionValueId,
        });
      }
    }

    formattedOptionsList.push(...Object.values(multipleSelectMap));
    return formattedOptionsList;
  }, []);

  // Fixed location click handler with proper API structure
  const handleLocationClick = useCallback(async (type: 'city' | 'district' | 'neighbourhood', id: number, name: string) => {
    if (!data) return;
    
    setIsLoading(true);
   try {
  const filterParams: FilterParams = {
    IncludeProperties: [ "City","User","Store","Currency","Country","District","Neighbourhood","AdvertMedia","AdvertMedia.Media","AdvertContactType","AdvertContactType.ContactType","User.UserPackage","User.UserPackage.Package"],
  };

  // Add category ID only if it exists and is valid
  if (data?.category?.id && typeof data.category.id === 'number') {
    filterParams.CategoryId = data.category.id;
  }

  // Add location parameters
  if (type === 'city' && id) {
    filterParams.CityId = id;
  } else if (type === 'district' && id) {
    if (data?.city?.id && typeof data.city.id === 'number') {
      filterParams.CityId = data.city.id;
    }
    filterParams.DistrictIds = [id];
  } else if (type === 'neighbourhood' && id) {
    if (data?.city?.id && typeof data.city.id === 'number') {
      filterParams.CityId = data.city.id;
    }
    if (data?.district?.id && typeof data.district.id === 'number') {
      filterParams.DistrictIds = [data.district.id];
    }
    filterParams.NeighbourhoodIds = [id];
  }

  if (Object.keys(filterParams).length === 0) {
    throw new Error('Geçerli filtre parametresi bulunamadı');
  }

  const response = await getAdvertListFilterSafe(filterParams);

  if (response?.success) {
    const routeState = {
      filterData: response.data,
      appliedFilters: filterParams,
      locationInfo: { type, id, name },
      categoryId: data?.category?.id,
      categoryName: data?.category?.name,
      cityId: data?.city?.id,
      cityName: data?.city?.name,
      districtId: data?.district?.id,
      districtName: data?.district?.name,
      neighbourhoodId: type === 'neighbourhood' ? id : data?.neighbourhood?.id,
      neighbourhoodName: type === 'neighbourhood' ? name : data?.neighbourhood?.name,
      timestamp: Date.now()
    };

    navigate(fullUrl, {
      state: routeState,
      replace: false
    });

    // openNotificationWithIcon(
    //   "default",
    //   `${name} bölgesindeki ${data?.category?.name || 'kategori'} ilanları listeleniyor...`
    // );
  } else {
    throw new Error('API response indicates failure');
  }
} catch (error) {
  console.error('Location filter error:', error);
  // openNotificationWithIcon(
  //   "default",
  //   "İlanlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
  // );
} finally {
  setIsLoading(false);
}

  }, [data, navigate, fullUrl, getAdvertListFilterSafe]);

  // Generate location link with click handler
  const generateLocationLink = useCallback(() => {
    if (!data) return null;

    const locationParts = [];
    if (data?.neighbourhood?.name) {
      locationParts.push(
        <button
          key="neighbourhood"
          type="button"
          className="hover:underline text-blue-600 bg-transparent border-none cursor-pointer p-0"
          onClick={() => handleLocationClick('neighbourhood', data.neighbourhood!.id, data.neighbourhood!.name)}
          disabled={isLoading}
          aria-label={`${data.neighbourhood.name} bölgesindeki ilanları görüntüle`}
        >
          {data.neighbourhood.name}
        </button>
      );
    }
    
    if (data?.district?.name) {
      if (locationParts.length > 0) locationParts.push('/ ');
      locationParts.push(
        <button
          key="district"
          type="button"
          className="hover:underline text-blue-600 bg-transparent border-none cursor-pointer p-0"
          onClick={() => handleLocationClick('district', data.district!.id, data.district!.name)}
          disabled={isLoading}
          aria-label={`${data.district.name} ilçesindeki ilanları görüntüle`}
        >
          {data.district.name}
        </button>
      );
    }
    
    if (data?.city?.name) {
      if (locationParts.length > 0) locationParts.push('/ ');
      locationParts.push(
        <button
          key="city"
          type="button"
          className="hover:underline text-blue-600 bg-transparent border-none cursor-pointer p-0"
          onClick={() => handleLocationClick('city', data.city!.id, data.city!.name)}
          disabled={isLoading}
          aria-label={`${data.city.name} şehrindeki ilanları görüntüle`}
        >
          {data.city.name}
        </button>
      );
    }

    return locationParts.length > 0 ? <Text>{locationParts}</Text> : null;
  }, [data, handleLocationClick, isLoading]);

  // Modal handlers
  const handleCloseAskQuesModal = useCallback(() => setIsShowAskQuesModal(false), []);
  const handleCloseCreateContactModal = useCallback(() => setIsShowCreateContactModal(false), []);
  const handleCloseComplaintModal = useCallback(() => setIsShowComplaintModal(false), []);

  const handleMessageClick = useCallback(() => {
    if (userInfoes) {
      setIsShowAskQuesModal(true);
    } else {
      openNotificationWithIcon(
        "warning",
        "Mesaj göndermek için giriş yapmanız gerekiyor"
      );
    }
  }, [userInfoes]);

  // Effects
  useEffect(() => {
    if (data?.advertOptionAttr) {
      const options = handleFormattedDBOptions(data.advertOptionAttr);
      setOptionsData(options);
    }
  }, [data, handleFormattedDBOptions]);


  // Combined effect for URL parameter handling
  useEffect(() => {
    const categoryId = searchParams.get('CategoryId');
    const cityId = searchParams.get('CityId');
    const districtId = searchParams.get('DistrictId');
    const neighbourhoodId = searchParams.get('NeighbourhoodId');

    if (categoryId || cityId || districtId || neighbourhoodId) {
      const filterParams: FilterParams = {};
      
      // Parse and validate parameters
      if (categoryId && !isNaN(parseInt(categoryId))) {
        filterParams.CategoryId = parseInt(categoryId);
      }
      if (cityId && !isNaN(parseInt(cityId))) {
        filterParams.CityId = parseInt(cityId);
      }
      if (districtId && !isNaN(parseInt(districtId))) {
        filterParams.DistrictIds = [parseInt(districtId)];
      }
      if (neighbourhoodId && !isNaN(parseInt(neighbourhoodId))) {
        filterParams.NeighbourhoodIds = [parseInt(neighbourhoodId)];
      }

      // Only make API call if we have valid parameters
      if (Object.keys(filterParams).length > 0) {
        getAdvertListFilterSafe(filterParams).catch(error => {
          console.error('URL parameter filter error:', error);
          openNotificationWithIcon(
            "error",
            "Filtreleme sırasında bir hata oluştu"
          );
        });
      }
    }
  }, [searchParams]);

  // Loading state
  if (!data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  // Check if user can contact

  const canContact = data?.advertContactType?.some(
    (item: any) => item.contactTypeId === 1 || item.contactTypeId === 2
  );

  const canMessage = data?.advertContactType?.some(
    (item: any) => item.contactTypeId === 2
  );
  return (
    <>
      <Row gutter={[10, 10]} className="!sticky !top-[20px] !bottom-[20px]">
        <Col span={24}>
          <div className="!flex !flex-col gap-4">
            <div className="justify-end !hidden lg:!flex !h-full !py-2">
              <CategoryBreadCrumb />
            </div>
            
            {/* Main Info Card */}
            <div className="bg-white border p-3 radius-xxl mt-4" style={{ borderRadius: '6px', borderBottom: '0px' }}>
              <div className="!flex gap-4 items-center">
                <Text className="!text-gray-500">
                  İlan Tarihi:{" "}
                  <span className="!font-normal">
                    {dayjs(data?.insertDate).format("DD.MM.YYYY")}
                  </span>
                </Text>
                <Text className="!text-gray-500">
                  İlan No: <span className="!font-normal">{data?.no}</span>
                </Text>
              </div>

              <div className="!flex justify-between items-center">
                <Text style={{ fontSize: "28px" }} className="!font-bold">
                  <PriceFormatter
                    inputPrice={data?.price}
                    currency={data?.currency?.shortName}
                    classNames="!text-xl"
                  />
                </Text>
              </div>

              <div className="mt-5">
                <Text className="!text-black !text-lg !font-bold">
                  {capitalize(data?.title || '')}
                </Text>
              </div>
              
              <div>
                {generateLocationLink()}
              </div>
            </div>
          </div>
        </Col>
        
        <div className="items-center sm:justify-end gap-2 !flex xl:!hidden">
          {userInfoes?.id !== data?.userId && <Options />}
        </div>
        
        <Col span={24}>
          {/* User Info Card */}
          <div className="!flex justify-between border bg-white p-3" style={{ borderRadius: '6px', borderBottom: '0px' }}>
            <div className="flex justify-center items-center">
              {data?.user.photoUrl ?(
                <img style={{ width: '50px', borderRadius:'50%', height:'50px', objectFit:'cover' }} src={`${setBackEndUlr()}/${data?.user.photoUrl.replace(/\\/g, "/")}`} alt="" />  
            ):
             <Avatar
                size={40}
                src={data?.user.photoUrl || undefined}
                alt={`${data?.user?.name || ""} ${data?.user?.surname || ""}`}
              >
                {`${data?.user?.name?.[0] ?? ""}${data?.user?.surname?.[0] ?? ""}`.toUpperCase()}
              </Avatar>
            }
            </div>

            <div className="!grid !gap-2 items-center justify-end !mt-0">
              {data?.store?.storeName && (
                <div className="!flex items-end !h-full !w-full justify-end">
                  <Text className="!text-base">{data.store.storeName}</Text>
                </div>
              )}
              
              <div className="!flex items-end !h-full !w-full justify-end flex-col">
                <Text className="mb-0 text-gray-400">İlan Sahibi</Text>
                <Text>{`${data?.user?.name || ""} ${data?.user?.surname || ""}`}</Text>
              </div>
              
              {userInfoes?.id !== data?.userId && (
                <>
                  {canContact ? (
                    <div className="!flex gap-2">
                      {canMessage && (
                        <Button
                          type="primary"
                          size="small"
                          className="!bg-[#ffc502] !p-2"
                          onClick={handleMessageClick}
                          loading={isLoading}
                          aria-label="Mesaj gönder"
                        >
                          <Space>
                            <QuestionCircleOutlined className="!text-black" />
                            <Text className="!text-black">Mesaj</Text>
                          </Space>
                        </Button>
                      )}
                      <Button
                        type="primary"
                        size="small"
                        className="!p-2 !bg-[#dce8ff]"
                        onClick={() => setIsShowCreateContactModal(true)}
                        aria-label="Telefon numarasını görüntüle"
                      >
                        <Space>
                          <PhoneOutlined className="!text-[#000]" />
                          <Text className="!text-black">Ara</Text>
                        </Space>
                      </Button>
                    </div>
                  ) : (
                    <Text className="text-red-500 !text-xs">
                      İletişim Tercihi Seçilmemiş
                    </Text>
                  )}
                </>
              )}
            </div>
          </div>
        </Col>
        
        {/* Options Card */}
       {optionsData?.length > 0 && (
  <Col span={24}>
    <div
      className="!flex !flex-col gap-2 border bg-white p-3"
      style={{ borderRadius: '6px', borderBottom: '0px' }}
    >
      {Array.from(
        new Map(
          optionsData
            ?.filter((item: FormattedOption) => item?.value)
            .map((item) => [item.key, item])
        ).values()
      ).map((item: FormattedOption, index: number, arr: FormattedOption[]) => {
        const isLast = index === arr.length - 1;
        return (
          <Col
            key={item.key || index}
            xs={24}
            className="!flex items-center gap-2 justify-between"
            style={{
              borderBottom: isLast ? "none" : "0.5px solid #e5e7eb",
            }}
          >
            <Text className="!font-bold">
              {item.name === "plate" ? "Araç Plakası" : item.name}
            </Text>
            {(() => {
              if (item.name === "m² (Brüt)" || item.name === "m² (Net)") {
                return <Text>{`${item.value} ${item.name}`}</Text>;
              } else if (item?.value) {
                return (
                  <Text>
                    {item?.value?.name ? item.value.name : item.value}
                  </Text>
                );
              }
              return null;
            })()}
          </Col>
        );
      })}
    </div>
  </Col>
)}

        
        {/* Complaint Link */}
        <Col xs={24}>
          <button
            type="button"
            className="!text-red-500 hover:cursor-pointer bg-transparent border-none p-0 hover:underline"
            onClick={() => setIsShowComplaintModal(true)}
            aria-label="İlanı şikayet et"
          >
            İlanı şikayet et
          </button>
        </Col>
      </Row>
      
      {/* Modals */}
      <Modal
        title={false}
        onCancel={handleCloseCreateContactModal}
        open={isShowCreateContactModal}
        footer={false}
        destroyOnClose
      >
        <CreateContactInfoes externalAdvert={data} />
      </Modal>
      
      <Modal
        title={false}
        onCancel={handleCloseComplaintModal}
        open={isShowComplaintModal}
        footer={false}
        destroyOnClose
      >
        <ComplaintForm onFinish={handleCloseComplaintModal} />
      </Modal>
      
      <Modal
        title={false}
        onCancel={handleCloseAskQuesModal}
        open={isShowAskQuesModal}
        footer={false}
        destroyOnClose
      >
        <SendMessage
          externalAdvert={data}
          onFinish={handleCloseAskQuesModal}
        />
      </Modal>
    </>
  );
};

export default RightInfoDetails;